// South African validation utilities for consent forms

/**
 * Validates South African ID number format
 * Format: YYMMDD SSSSS C A Z
 * - YYMMDD: Date of birth
 * - SSSSS: Gender (0000-4999 female, 5000-9999 male)
 * - C: Citizenship (0 SA citizen, 1 permanent resident)
 * - A: Usually 8, could be 9 if alien
 * - Z: Checksum digit
 */
export const validateSouthAfricanID = (idNumber) => {
  if (!idNumber) return { isValid: false, error: 'ID number is required' };
  
  // Remove spaces and hyphens
  const cleanId = idNumber.replace(/[\s-]/g, '');
  
  // Check if exactly 13 digits
  if (!/^\d{13}$/.test(cleanId)) {
    return { isValid: false, error: 'ID number must be exactly 13 digits' };
  }
  
  // Extract date components
  const yy = parseInt(cleanId.substring(0, 2));
  const mm = parseInt(cleanId.substring(2, 4));
  const dd = parseInt(cleanId.substring(4, 6));
  
  // Validate month
  if (mm < 1 || mm > 12) {
    return { isValid: false, error: 'Invalid month in ID number' };
  }
  
  // Validate day
  if (dd < 1 || dd > 31) {
    return { isValid: false, error: 'Invalid day in ID number' };
  }
  
  // Determine century (assuming current year logic)
  const currentYear = new Date().getFullYear();
  const currentCentury = Math.floor(currentYear / 100) * 100;
  const currentTwoDigits = currentYear % 100;
  
  let fullYear;
  if (yy <= currentTwoDigits) {
    fullYear = currentCentury + yy;
  } else {
    fullYear = currentCentury - 100 + yy;
  }
  
  // Validate date
  const dateOfBirth = new Date(fullYear, mm - 1, dd);
  if (dateOfBirth.getFullYear() !== fullYear || 
      dateOfBirth.getMonth() !== mm - 1 || 
      dateOfBirth.getDate() !== dd) {
    return { isValid: false, error: 'Invalid date in ID number' };
  }
  
  // Check if date is not in future
  if (dateOfBirth > new Date()) {
    return { isValid: false, error: 'ID number cannot have a future date' };
  }
  
  // Check if date is not too old (more than 120 years)
  const maxAge = new Date();
  maxAge.setFullYear(maxAge.getFullYear() - 120);
  if (dateOfBirth < maxAge) {
    return { isValid: false, error: 'ID number date is too far in the past' };
  }
  
  // Validate checksum using Luhn algorithm
  let sum = 0;
  let alternate = false;
  
  for (let i = cleanId.length - 1; i >= 0; i--) {
    let digit = parseInt(cleanId.charAt(i));
    
    if (alternate) {
      digit *= 2;
      if (digit > 9) {
        digit = Math.floor(digit / 10) + (digit % 10);
      }
    }
    
    sum += digit;
    alternate = !alternate;
  }
  
  if (sum % 10 !== 0) {
    return { isValid: false, error: 'Invalid ID number checksum' };
  }
  
  return { isValid: true, dateOfBirth };
};

/**
 * Validates South African contact number
 * Accepts formats: +27XXXXXXXXX, 0XXXXXXXXX, XXXXXXXXX (9 digits)
 */
export const validateSouthAfricanContact = (contactNumber) => {
  if (!contactNumber) return { isValid: false, error: 'Contact number is required' };
  
  // Remove spaces, hyphens, parentheses
  const cleanContact = contactNumber.replace(/[\s\-\(\)]/g, '');
  
  // Check if starts with +27
  if (cleanContact.startsWith('+27')) {
    const remaining = cleanContact.substring(3);
    if (!/^\d{9}$/.test(remaining)) {
      return { isValid: false, error: 'Invalid South African contact number format' };
    }
    return { isValid: true, formatted: '+27' + remaining };
  }
  
  // Check if starts with 0
  if (cleanContact.startsWith('0')) {
    if (!/^0\d{9}$/.test(cleanContact)) {
      return { isValid: false, error: 'Invalid South African contact number format' };
    }
    return { isValid: true, formatted: cleanContact };
  }
  
  // Check if exactly 9 digits (without prefix)
  if (/^\d{9}$/.test(cleanContact)) {
    return { isValid: true, formatted: '0' + cleanContact };
  }
  
  return { isValid: false, error: 'Invalid South African contact number format' };
};

/**
 * Validates date of birth format and logic
 * Accepts: YYYY-MM-DD format
 */
export const validateDateOfBirth = (dob) => {
  const normalized = normalizeDateInputToIso(dob);
  if (!normalized) {
    return { isValid: false, error: 'Date of birth is required' };
  }

  // Validate format YYYY-MM-DD
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(normalized)) {
    return { isValid: false, error: 'Date of birth must be in format DD-MM-YYYY' };
  }

  const [year, month, day] = normalized.split('-').map(num => parseInt(num, 10));
  const dobDate = new Date(year, month - 1, day);
  if (dobDate.getFullYear() !== year || 
      dobDate.getMonth() !== month - 1 || 
      dobDate.getDate() !== day) {
    return { isValid: false, error: 'Invalid date' };
  }

  // Check if date is not in future
  if (dobDate > new Date()) {
    return { isValid: false, error: 'Date of birth cannot be in the future' };
  }

  // Check if date is not too old (more than 120 years)
  const maxAge = new Date();
  maxAge.setFullYear(maxAge.getFullYear() - 120);
  if (dobDate < maxAge) {
    return { isValid: false, error: 'Date of birth is too far in the past' };
  }

  return { isValid: true, date: dobDate };
};

/**
 * Validates passport number (basic validation)
 */
export const validatePassportNumber = (value) => {
  if (!value) return { isValid: false, error: 'Passport number is required' };
  
  const cleanPassport = value.replace(/[\s-]/g, '');
  
  if (cleanPassport.length < 6 || cleanPassport.length > 15) {
    return { isValid: false, error: 'Passport number must be between 6 and 15 characters' };
  }
  
  if (!/^[A-Za-z0-9]+$/.test(cleanPassport)) {
    return { isValid: false, error: 'Passport number can only contain letters and numbers' };
  }
  
  return { isValid: true };
};

const normalizeDateInputToIso = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const m = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (!m) return raw;
  const [, dd, mm, yyyy] = m;
  return `${yyyy}-${mm}-${dd}`;
};

/**
 * Validates ID or passport number field
 */
export const validateIdOrPassport = (value) => {
  if (!value) return { isValid: false, error: 'ID number or passport is required' };
  
  const cleanValue = value.replace(/[\s-]/g, '');
  
  // Try South African ID validation first
  if (/^\d{13}$/.test(cleanValue)) {
    return validateSouthAfricanID(cleanValue);
  }
  
  // Try passport validation
  return validatePassportNumber(cleanValue);
};

/**
 * Validates ID or DOB field (accepts either ID number or date of birth)
 */
export const validateIdOrDob = (value) => {
  if (!value) return { isValid: false, error: 'ID number or date of birth is required' };

  const trimmed = String(value || "").trim();
  const cleanDigits = trimmed.replace(/\D/g, '');

  // Try South African ID validation first
  if (/^\d{13}$/.test(cleanDigits)) {
    return validateSouthAfricanID(cleanDigits);
  }

  const normalizedDate = normalizeDateInputToIso(trimmed);
  if (/^\d{4}-\d{2}-\d{2}$/.test(normalizedDate)) {
    return validateDateOfBirth(normalizedDate);
  }

  return { isValid: false, error: 'Please enter a valid 13-digit ID number or date of birth (DD-MM-YYYY)' };
};

export const validateAdultVaccinationConsentForm = (formData) => {
  const errors = {};

  if (!String(formData?.patientFullName || "").trim()) {
    errors.patientFullName = "Full name is required";
  }

  const idOrDobValidation = validateIdOrDob(formData?.patientIdOrDob);
  if (!idOrDobValidation.isValid) {
    errors.patientIdOrDob = idOrDobValidation.error;
  }

  const contactValidation = validateSouthAfricanContact(formData?.patientContactNumber);
  if (!contactValidation.isValid) {
    errors.patientContactNumber = contactValidation.error;
  }

  if (!formData?.dateOfVisit) {
    errors.dateOfVisit = "Date of visit is required";
  }

  if (!String(formData?.vaccineName || "").trim()) {
    errors.vaccineName = "Vaccine name is required";
  }

  if (!String(formData?.batchNumber || "").trim()) {
    errors.batchNumber = "Batch number is required";
  }

  if (!formData?.expiryDate) {
    errors.expiryDate = "Expiry date is required";
  }

  if (!String(formData?.patientSignature || "").trim()) {
    errors.patientSignature = "Patient signature is required";
  }

  if (!formData?.patientSignatureDate) {
    errors.patientSignatureDate = "Patient signature date is required";
  }

  if (!String(formData?.administratorName || "").trim()) {
    errors.administratorName = "Administrator name is required";
  }

  if (!formData?.administratorSignatureDate) {
    errors.administratorSignatureDate = "Administrator signature date is required";
  }

  if (!formData?.popiaConsent) {
    errors.popiaConsent = "POPIA consent is required";
  }

  return errors;
};

export const validateMinorVaccinationConsentForm = (formData) => {
  const errors = {};

  if (!String(formData?.childFullName || "").trim()) {
    errors.childFullName = "Child full name is required";
  }

  const dobValidation = validateDateOfBirth(formData?.childDob);
  if (!dobValidation.isValid) {
    errors.childDob = dobValidation.error;
  }

  if (!formData?.popiaConsent) {
    errors.popiaConsent = "POPIA consent is required";
  }

  if (!String(formData?.guardianSignature || "").trim()) {
    errors.guardianSignature = "Guardian signature is required";
  }

  if (!formData?.guardianSignatureDate) {
    errors.guardianSignatureDate = "Guardian signature date is required";
  }

  if (!String(formData?.guardianSignatureFullName || "").trim()) {
    errors.guardianSignatureFullName = "Guardian signature full name is required";
  }

  if (!String(formData?.nurseOrPharmacistSignature || "").trim()) {
    errors.nurseOrPharmacistSignature = "Nurse or pharmacist signature is required";
  }

  if (!formData?.nurseOrPharmacistSignatureDate) {
    errors.nurseOrPharmacistSignatureDate = "Nurse or pharmacist signature date is required";
  }

  return errors;
};

export const validateFamilyPlanningConsentForm = (formData) => {
  const errors = {};

  if (!String(formData?.patientFullName || "").trim()) {
    errors.patientFullName = "Full name is required";
  }

  const idValidation = validateIdOrPassport(formData?.patientIdOrPassport);
  if (!idValidation.isValid) {
    errors.patientIdOrPassport = idValidation.error;
  }

  const dobValidation = validateDateOfBirth(formData?.patientDob);
  if (!dobValidation.isValid) {
    errors.patientDob = dobValidation.error;
  }

  const contactValidation = validateSouthAfricanContact(formData?.patientCellphone);
  if (!contactValidation.isValid) {
    errors.patientCellphone = contactValidation.error;
  }

  if (!String(formData?.patientAddress || "").trim()) {
    errors.patientAddress = "Address is required";
  }

  if (!String(formData?.injectionType || "").trim()) {
    errors.injectionType = "Injection type is required";
  }

  if (!formData?.dateGiven) {
    errors.dateGiven = "Date given is required";
  }

  if (!String(formData?.batch || "").trim()) {
    errors.batch = "Batch number is required";
  }

  if (!formData?.nextDueDate) {
    errors.nextDueDate = "Next due date is required";
  }

  if (!formData?.expiry) {
    errors.expiry = "Expiry date is required";
  }

  if (!String(formData?.patientSignature || "").trim()) {
    errors.patientSignature = "Patient signature is required";
  }

  if (!formData?.patientSignatureDate) {
    errors.patientSignatureDate = "Patient signature date is required";
  }

  if (!String(formData?.pharmacistSignature || "").trim() && !String(formData?.nurseSignature || "").trim()) {
    errors.practitionerSignature = "Either pharmacist or nurse signature is required";
  }

  if (!formData?.pharmacistSignatureDate && !formData?.nurseSignatureDate) {
    errors.practitionerSignatureDate = "Practitioner signature date is required";
  }

  if (!formData?.popiaConsent) {
    errors.popiaConsent = "POPIA consent is required";
  }

  return errors;
};

export const validateInjectionAdministrationConsentForm = (formData) => {
  const errors = {};

  if (!String(formData?.patientFullName || "").trim()) {
    errors.patientFullName = "Full name is required";
  }

  const idOrDobValidation = validateIdOrDob(formData?.patientIdOrDob);
  if (!idOrDobValidation.isValid) {
    errors.patientIdOrDob = idOrDobValidation.error;
  }

  const contactValidation = validateSouthAfricanContact(formData?.patientContactNumber);
  if (!contactValidation.isValid) {
    errors.patientContactNumber = contactValidation.error;
  }

  if (!formData?.dateOfVisit) {
    errors.dateOfVisit = "Date of visit is required";
  }

  if (!String(formData?.patientAddress || "").trim()) {
    errors.patientAddress = "Address is required";
  }

  if (!String(formData?.injectionMedicineName || "").trim()) {
    errors.injectionMedicineName = "Injection/medicine name is required";
  }

  if (!String(formData?.batchNumber || "").trim()) {
    errors.batchNumber = "Batch number is required";
  }

  if (!formData?.expiryDate) {
    errors.expiryDate = "Expiry date is required";
  }

  const route = String(formData?.routeOfAdministration || "").trim();
  if (!route) {
    errors.routeOfAdministration = "Route of administration is required";
  } else if (route !== "IM" && route !== "SC") {
    errors.routeOfAdministration = "Route of administration must be IM or SC";
  }

  if (!String(formData?.administratorName || "").trim()) {
    errors.administratorName = "Administrator name is required";
  }

  if (!formData?.administratorSignatureDate) {
    errors.administratorSignatureDate = "Administrator signature date is required";
  }

  if (!String(formData?.patientSignature || "").trim()) {
    errors.patientSignature = "Patient signature is required";
  }

  if (!formData?.patientSignatureDate) {
    errors.patientSignatureDate = "Patient signature date is required";
  }

  if (!formData?.voluntaryConsent) {
    errors.voluntaryConsent = "Voluntary consent is required";
  }

  if (!formData?.popiaConsent) {
    errors.popiaConsent = "POPIA consent is required";
  }

  return errors;
};
